import { AfterViewInit, Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { CredentialService } from "../../credential.service";
import { HttpClient } from "@angular/common/http";
import { Urls } from "../../shared/urls";
import { Subject } from "rxjs";
import { NotifyService } from "../../services/notify.service";
import { TranslateService } from "@ngx-translate/core";
import validator from "validator";
import { MessageService } from "../../services/message.service";
import { redirectIfAllowed } from "../../utils/redirect.util";

declare var $;

@Component({
  standalone: false,
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["login.component.scss"]
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  loginForm: FormGroup;
  public checkLoginStrategyForm: FormGroup;
  passwordForm: FormGroup;
  forgotForm: FormGroup;
  errorMsg;
  status = "login";
  returnUrl;
  id;
  token;
  isLoading = false;
  authCode = "";
  TFAData;
  public userNamePasswordStrategy: boolean = false;
  public checkForLoginStrategy: boolean = true;
  public loginStrategyLoading: boolean = true;
  public isValidEmail: boolean = false;
  private unsubscribe: Subject<void> = new Subject();
  public isResetSuccessfully: boolean = false;
  public showPassword: boolean = false;
  public showResetNewPassword: boolean = false;
  public showResetConfirmPassword: boolean = false;

  constructor(
    private formBuild: FormBuilder,
    private credentialService: CredentialService,
    private http: HttpClient,
    private _notificationService: NotifyService,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    if (localStorage.getItem("user") && localStorage.getItem("token")) {
      this.isLoading = true;
      this.router.navigate(["dashboard"]);
    } else {
      if (this.route.snapshot.paramMap.get("id") && this.route.snapshot.queryParams["token"]) {
        if (this.router.url.includes("set-password")) {
          this.status = "set-new";
        } else {
          this.status = "reset";
          this.passwordForm = this.formBuild.group(
            {
              new: ["", [Validators.required]],
              confirm: ["", Validators.required]
            },
            {
              validator: RegistrationValidator.validate.bind(this)
            }
          );
        }
        this.id = this.route.snapshot.paramMap.get("id");
        this.token = this.route.snapshot.queryParams["token"];
      } else {
        this.returnUrl = this.route.snapshot.queryParams["returnUrl"] || "/";
      }
      // localStorage clear then and then no have detail of user or token
      localStorage.clear();
    }

    this.checkLoginStrategyForm = this.formBuild.group({
      email: ["", [Validators.required, Validators.email]]
    });

    this.loginForm = this.formBuild.group({
      email: ["", [Validators.required, Validators.email]],
      password: ["", Validators.required]
    });

    if (this.router.url.includes("/forgot")) {
      this.forgotForm = this.formBuild.group({
        email: [this.route.snapshot.queryParams.email, [Validators.required, Validators.email]]
      });
      this.status = "forgot";
    }
    if (this.status === "login") {
      this.loginForm.controls.email.setValue(this.route.snapshot.queryParams.email);
    }
  }

  // load the entire login page then remove loader
  ngAfterViewInit(): void {
    // user and token have not any detail otherwise redirect the dashboard so no need to remove loader
    if (!(localStorage.getItem("user") && localStorage.getItem("token"))) {
      setTimeout(() => {
        if (document.getElementById("initial_loader")) {
          document.getElementById("initial_loader").remove();
          this.isLoading = false;
        }
      }, 100);
    }
  }

  resetLoginForm() {
    this.status = "login";
    this.loginForm.controls["password"].setValue("");
    this.loginForm.controls["email"].setValue("");
  }

  public doLoginStrategy(): void {
    if (this.checkLoginStrategyForm.valid) {
      this.loginStrategyLoading = false;
      const loginData: { email: string } = {
        email: this.checkLoginStrategyForm.value.email.trim().toLowerCase()
      };

      this.credentialService.isAzureSSOEnabled(loginData).subscribe(
        (azureSSOResponse: { redirect_url?: string }): void => {
          this.loginStrategyLoading = true;
          if (azureSSOResponse.redirect_url) {
            redirectIfAllowed(azureSSOResponse.redirect_url);
            return;
          }
          this.checkForLoginStrategy = false;
          this.loginForm.controls["email"].setValue(loginData.email);
          this.userNamePasswordStrategy = true;
        },
        azureError => {
          console.error(":::LOGIN | doLoginStrategy():::", azureError, "::::::");
        }
      );
    }
  }

  async doLogin() {
    if (this.loginForm.valid) {
      const data = {
        email: this.loginForm.value.email.trim().toLowerCase(),
        password: this.loginForm.value.password.trim(),
        isAppPanel: true
      };
      this.credentialService.login(data).subscribe(
        async (loginResponse): Promise<void> => {
          if (loginResponse.user) {
            this.doNavigate();
            return;
          }
          if (loginResponse.isUser2FA) {
            this.loginStrategyLoading = true;
            this.TFAData = loginResponse;
            this.status = "acceptOTP";
            return;
          }
          if (loginResponse.isLawFirm2FA) {
            const a = loginResponse.token;
            this.http.get(Urls.TFA, { headers: { authorization: a } }).subscribe(
              TFASetup => {
                this.loginStrategyLoading = true;
                this.TFAData = TFASetup;
                this.status = "setupTFA";
              },
              err => {
                console.log(":::login:::doLogin:::error:::", err);
              }
            );
          }
        },
        loginError => {
          this.loginStrategyLoading = true;
          console.error(":::LOGIN | doLogin():::", loginError, "::::::");
          if (loginError.error.code) {
            this.errorMsg = loginError.error.code;
          } else if (loginError.error.message) {
            this.errorMsg = loginError.error.message;
          }
        }
      );
    }
  }

  doNavigate() {
    this.isLoading = true;
    this.router.navigateByUrl(this.returnUrl !== "/" && this.returnUrl ? this.returnUrl : "");
  }

  enableTFA(flag?) {
    if (this.authCode.trim() !== "") {
      this.http
        .put(
          Urls.TFA,
          {
            code: this.authCode,
            isAppPanel: true
          },
          { headers: { authorization: this.TFAData.token } }
        )
        .subscribe(
          (TFASetup: any) => {
            this.authCode = "";
            this.credentialService.setLogin(TFASetup);
            if (flag) {
              this._notificationService.success("Successfully Integrated");
              setTimeout(() => this.doNavigate(), 3000);
            } else {
              this.doNavigate();
            }
          },
          err => {
            this.errorMsg = "INVALID_CODE";
            console.log(":::login:::enableTFA:::error:::", err);
          }
        );
    }
  }

  removeError(event?) {
    if (!event || event.key !== "Enter") {
      this.errorMsg = "";
    }
    if (
      validator.isEmail(this.checkLoginStrategyForm.controls["email"].value) ||
      (this.forgotForm && validator.isEmail(this.forgotForm.controls["email"].value))
    ) {
      this.isValidEmail = true;
      return;
    }
    this.isValidEmail = false;
  }

  async forgot() {
    if (this.forgotForm.valid) {
      const data = Object.assign({}, this.forgotForm.value);
      data.email = data.email.toLowerCase().trim();
      data["isAppPanel"] = true;
      try {
        const res: any = await this.credentialService.checkForgotEmail(data);
        if (res && res.success) {
          this.isResetSuccessfully = true;
        }
      } catch (e) {
        this.isResetSuccessfully = false;
        this.errorMsg = e.error.code;
      } finally {
        this.forgotForm.reset();
      }
    }
  }

  async resetPassword() {
    try {
      const res: any = await this.credentialService.resetPassword(this.id, {
        token: this.token,
        password: this.passwordForm.controls.new.value
      });
      if (res && res.success) {
        this.messageService
          .fire({
            titleText: this.translateService.instant("login.Password_Changed"),
            text: this.translateService.instant("login.Your_Password_has_been_changed"),
            icon: "success"
          })
          .then(result => {
            if (result.value) {
              this.router.navigate(["login"]);
            }
          });
      }
    } catch (e) {
      console.error(e);
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  sendMailTo() {
    window.location.href = "mailto:info@lawren.io";
  }

  checkForPasswordStrength(event) {
    const pass = event.target.value;
    const regEx = /((?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\W]).{8,100})/g;
    if (!regEx.test(pass)) {
      this.errorMsg = "PASSWORD_WEAK";
    } else {
      this.errorMsg = "";
    }
  }

  checkForDisable(): boolean {
    let rr = true;
    if (!this.passwordForm.invalid && this.errorMsg === "") {
      rr = false;
    }
    return rr;
  }

  get isResetNewPasswordEmpty(): boolean {
    return !this.passwordForm || !this.passwordForm.controls.new.value;
  }

  get isResetConfirmPasswordEmpty(): boolean {
    return !this.passwordForm || !this.passwordForm.controls.confirm.value;
  }

  toggleResetNewPasswordVisibility(): void {
    if (this.isResetNewPasswordEmpty) {
      return;
    }
    this.showResetNewPassword = !this.showResetNewPassword;
  }

  toggleResetConfirmPasswordVisibility(): void {
    if (this.isResetConfirmPasswordEmpty) {
      return;
    }
    this.showResetConfirmPassword = !this.showResetConfirmPassword;
  }
}

export class RegistrationValidator {
  /**
   * Custom validator for the password/reset form.
   *
   * The built‑in `Validators.required` already marks both fields as required, so
   * this function only checks whether the two values are equal.  If they
   * differ an error object with a clear name is returned; otherwise `null`.
   */
  static validate(passwordForm: FormGroup) {
    const password = passwordForm.controls.new.value;
    const confirm = passwordForm.controls.confirm.value;

    // don't flag an error while the user is still typing
    // (required validator will take care of empty case)
    if (!confirm) {
      return null;
    }

    if (password !== confirm) {
      return { passwordMismatch: true };
    }

    return null;
  }
}
